import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../index.css"; // For scrollbar-hide utility
import {
  getRememberedEmails,
  deleteRememberedEmail,
  updateRememberedEmailNotes,
  RememberedEmailFilters,
  getApartmentsForFilter,
} from "./DataServices/EmailDraftService";
import {
  ArrowLeftIcon,
  BookmarkIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import Loading from "./Loading";
import MessageBox from "./MessageBox";

interface Apartment {
  id: string;
  title: string;
}

interface RememberedEmail {
  id: string;
  senderEmail: string;
  receiverEmail: string;
  listingId: string | null;
  subject: string;
  editedBody: string;
  originalBody: string;
  differenceSummary: string | null;
  notes: string | null;
  showingStatus: string | null;
  appfolioStatus: string | null;
  createdAt: string;
  updatedAt: string | null;
}

interface OverallStats {
  learnedEmails: number;
  totalSentEmails: number;
  rememberedEmails: number;
  learningProgress: string;
  learningPercentage: number;
  emailAccuracy: number;
  learningAccuracy: number;
}

interface RememberedEmailsResponse {
  emails: RememberedEmail[];
  overallStats: OverallStats;
}

interface Filters {
  types: string[];
  showingStatus: string[];
  appfolioStatus: string[];
  apartments: string[];
}

const defaultFilters: Filters = {
  types: [],
  showingStatus: [],
  appfolioStatus: [],
  apartments: [],
};

export default function NotesPage() {
  const navigate = useNavigate();

  const [rememberedEmails, setRememberedEmails] = useState<RememberedEmail[]>(
    [],
  );
  const [overallStats, setOverallStats] = useState<OverallStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [editedNotes, setEditedNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  // Filter states
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Apartment states
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [showApartmentDropdown, setShowApartmentDropdown] = useState(false);
  const apartmentDropdownRef = useRef<HTMLDivElement>(null);

  // Scroll state for right panel fade indicator
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setIsAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 32);
  };

  // Reset scroll indicator when emails change
  useEffect(() => {
    setIsAtBottom(false);
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [rememberedEmails]);

  useEffect(() => {
    loadRememberedEmails();
    loadApartments();
  }, []);

  // Close apartment dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        apartmentDropdownRef.current &&
        !apartmentDropdownRef.current.contains(e.target as Node)
      ) {
        setShowApartmentDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reload when filters change
  useEffect(() => {
    loadRememberedEmails();
  }, [filters]);

  const loadApartments = async () => {
    try {
      const data = await getApartmentsForFilter();
      if (data && Array.isArray(data)) {
        setApartments(
          data.map((apt: any) => ({
            id: apt.id || apt.ID,
            title: apt.title || apt.Title || "Unnamed",
          })),
        );
      }
    } catch (error) {
      console.error("Failed to load apartments:", error);
    }
  };

  // Backend already returns only apartments linked to remembered emails
  const availableApartments = apartments;

  const buildApiFilters = (): RememberedEmailFilters => {
    const apiFilters: RememberedEmailFilters = {};

    if (filters.types.length > 0) apiFilters.types = filters.types;

    if (filters.showingStatus.length > 0)
      apiFilters.showingStatuses = filters.showingStatus;

    if (filters.appfolioStatus.length > 0)
      apiFilters.appfolioStatuses = filters.appfolioStatus;

    if (filters.apartments.length > 0)
      apiFilters.apartments = filters.apartments;

    return apiFilters;
  };

  const loadRememberedEmails = async () => {
    try {
      setLoading(true);
      const apiFilters = buildApiFilters();
      const data: RememberedEmailsResponse =
        await getRememberedEmails(apiFilters);
      setRememberedEmails(data.emails || []);
      setOverallStats(data.overallStats);
    } catch (error) {
      console.error("Failed to load remembered emails:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFilter = (
    category: keyof Omit<Filters, "apartment">,
    value: string,
  ) => {
    setFilters((prev) => {
      const current = prev[category] as string[];
      const newValues = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [category]: newValues };
    });
  };

  const handleToggleApartment = (title: string) => {
    setFilters((prev) => {
      const current = prev.apartments;
      const updated = current.includes(title)
        ? current.filter((a) => a !== title)
        : [...current, title];
      return { ...prev, apartments: updated };
    });
  };

  const handleClearApartments = () => {
    setFilters((prev) => ({ ...prev, apartments: [] }));
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  const hasActiveFilters =
    filters.types.length > 0 ||
    filters.showingStatus.length > 0 ||
    filters.appfolioStatus.length > 0 ||
    filters.apartments.length > 0;

  const handleDelete = (id: string) => {
    setItemToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    setDeletingId(itemToDelete);
    setShowDeleteConfirm(false);
    try {
      await deleteRememberedEmail(itemToDelete);
      setRememberedEmails((prev) => prev.filter((e) => e.id !== itemToDelete));
    } catch (error) {
      console.error("Failed to delete:", error);
    } finally {
      setDeletingId(null);
      setItemToDelete(null);
    }
  };

  const handleToggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleEditNotes = (email: RememberedEmail) => {
    setEditingNotesId(email.id);
    setEditedNotes(email.notes || "");
  };

  const handleSaveNotes = async (id: string) => {
    setSavingNotes(true);
    try {
      await updateRememberedEmailNotes(id, editedNotes);
      setRememberedEmails((prev) =>
        prev.map((e) => (e.id === id ? { ...e, notes: editedNotes } : e)),
      );
      setEditingNotesId(null);
    } catch (error) {
      console.error("Failed to save notes:", error);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleCancelEditNotes = () => {
    setEditingNotesId(null);
    setEditedNotes("");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="h-screen bg-[#f3f3f3] flex flex-col">
      {/* HEADER (page title/back, no global Navbar) */}
      <div className="bg-[#f3f3f3] border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-7 h-7 flex items-center justify-center bg-black text-white rounded-md hover:bg-gray-900 flex-shrink-0"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
            <BookmarkIcon className="w-5 h-5 text-gray-600 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base font-medium text-black truncate">
                Learning Notes
              </h1>
              <p className="text-[10px] text-gray-500 mt-0.5 hidden sm:block">
                Review your email editing patterns and learnings
              </p>
            </div>
          </div>

          {/* Mobile Filter Toggle */}
          <button
            type="button"
            onClick={() => setMobileFiltersOpen((prev) => !prev)}
            className="md:hidden px-3 py-1.5 rounded-md border border-gray-300 bg-white text-[11px] font-medium text-gray-800 shadow-sm hover:bg-gray-50 inline-flex items-center gap-1.5 flex-shrink-0"
          >
            <FunnelIcon className="w-4 h-4" />
            Filters
          </button>
        </div>
      </div>

      {/* OVERALL LEARNING STATS */}
      {overallStats && (
        <div className="bg-[#f3f3f3] px-4 sm:px-6 py-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center shadow-sm">
                  <BookmarkIcon className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-900">
                    Overall Learning Progress
                  </h2>
                  <p className="text-[10px] text-gray-500">
                    Email editing improvement over time
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">
                    {overallStats.learningProgress}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    learning / total
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-700">
                    {overallStats.learningPercentage}%
                  </div>
                  <div className="text-[10px] text-gray-500">learning rate</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-700">
                    {overallStats.emailAccuracy}%
                  </div>
                  <div className="text-[10px] text-gray-500">accuracy</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MAIN LAYOUT - Responsive (like EmailInbox) */}
      <div className="flex-1 flex flex-col md:flex-row gap-4 px-4 pb-4 pt-3 overflow-hidden min-h-0">
        {/* LEFT SIDEBAR - FILTERS (toggle on mobile, always on desktop) */}
        <div
          className={`${mobileFiltersOpen ? "block" : "hidden"} md:flex md:flex-col md:h-full`}
        >
          <div className="relative w-full md:w-52 flex-shrink-0 max-h-64 md:max-h-none md:flex-1 md:min-h-0">
            <div className="w-full h-full bg-white rounded-2xl shadow-sm border border-gray-200 py-5 px-5 overflow-y-auto scrollbar-hide">
              {/* Filter Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FunnelIcon className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-semibold text-gray-900">
                    Filters
                  </span>
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-[10px] text-gray-500 hover:text-gray-700 flex items-center gap-1 transition"
                  >
                    <XMarkIcon className="w-3 h-3" />
                    Clear
                  </button>
                )}
              </div>

              {/* Apartment Filter - Dropdown */}
              <div ref={apartmentDropdownRef} className="relative mb-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-900">
                    Apartment
                  </h3>
                  {filters.apartments.length > 0 && (
                    <button
                      onClick={handleClearApartments}
                      className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-0.5 transition"
                    >
                      <XMarkIcon className="w-3 h-3" />
                      Clear
                    </button>
                  )}
                </div>
                {/* Dropdown toggle button */}
                <button
                  type="button"
                  onClick={() => setShowApartmentDropdown((prev) => !prev)}
                  className="w-full flex items-center justify-between px-2.5 py-1.5 text-xs border border-gray-200 rounded bg-white hover:border-gray-400 focus:outline-none focus:border-gray-400 transition"
                >
                  <span
                    className={
                      filters.apartments.length > 0
                        ? "text-gray-900 font-medium truncate"
                        : "text-gray-400"
                    }
                  >
                    {filters.apartments.length === 0
                      ? "Select..."
                      : filters.apartments.length === 1
                        ? filters.apartments[0]
                        : `${filters.apartments.length} selected`}
                  </span>
                  <ChevronDownIcon
                    className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-1 transition-transform ${
                      showApartmentDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {/* Dropdown list */}
                {showApartmentDropdown && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto">
                    {availableApartments.length === 0 ? (
                      <div className="px-2.5 py-2 text-[10px] text-gray-400 text-center italic">
                        No apartments
                      </div>
                    ) : (
                      availableApartments.map((apt) => (
                        <div
                          key={apt.id}
                          onClick={() => handleToggleApartment(apt.title)}
                          className={`flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer hover:bg-gray-50 transition ${
                            filters.apartments.includes(apt.title)
                              ? "bg-gray-50"
                              : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            readOnly
                            checked={filters.apartments.includes(apt.title)}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 pointer-events-none"
                          />
                          <span
                            className={`truncate ${
                              filters.apartments.includes(apt.title)
                                ? "text-gray-900 font-medium"
                                : "text-gray-600"
                            }`}
                          >
                            {apt.title}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Email Type */}
              <div className="mb-5">
                <h3 className="text-xs font-semibold text-gray-900 mb-2">
                  Email Type
                </h3>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.types.includes("streeteasy")}
                      onChange={() => handleToggleFilter("types", "streeteasy")}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 focus:ring-gray-500 focus:ring-offset-0"
                    />
                    <span
                      className={`text-xs group-hover:text-gray-900 ${filters.types.includes("streeteasy") ? "text-gray-900 font-medium" : "text-gray-600"}`}
                    >
                      Streeteasy
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.types.includes("queries")}
                      onChange={() => handleToggleFilter("types", "queries")}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 focus:ring-gray-500 focus:ring-offset-0"
                    />
                    <span
                      className={`text-xs group-hover:text-gray-900 ${filters.types.includes("queries") ? "text-gray-900 font-medium" : "text-gray-600"}`}
                    >
                      Queries
                    </span>
                  </label>
                </div>
              </div>

              {/* Showing Status */}
              <div className="mb-5">
                <h3 className="text-xs font-semibold text-gray-900 mb-2">
                  Showing Status
                </h3>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.showingStatus.includes("Inquired")}
                      onChange={() =>
                        handleToggleFilter("showingStatus", "Inquired")
                      }
                      className="w-3.5 h-3.5 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 focus:ring-offset-0"
                    />
                    <span
                      className={`text-xs group-hover:text-gray-900 ${filters.showingStatus.includes("Inquired") ? "text-yellow-700 font-medium" : "text-gray-600"}`}
                    >
                      Inquired
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.showingStatus.includes("Confirmed")}
                      onChange={() =>
                        handleToggleFilter("showingStatus", "Confirmed")
                      }
                      className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                    />
                    <span
                      className={`text-xs group-hover:text-gray-900 ${filters.showingStatus.includes("Confirmed") ? "text-blue-700 font-medium" : "text-gray-600"}`}
                    >
                      Confirmed
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.showingStatus.includes("Seen")}
                      onChange={() =>
                        handleToggleFilter("showingStatus", "Seen")
                      }
                      className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500 focus:ring-offset-0"
                    />
                    <span
                      className={`text-xs group-hover:text-gray-900 ${filters.showingStatus.includes("Seen") ? "text-green-700 font-medium" : "text-gray-600"}`}
                    >
                      Seen
                    </span>
                  </label>
                </div>
              </div>

              {/* Application Status */}
              <div className="mb-5">
                <h3 className="text-xs font-semibold text-gray-900 mb-2">
                  Application Status
                </h3>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.appfolioStatus.includes("Not Started")}
                      onChange={() =>
                        handleToggleFilter("appfolioStatus", "Not Started")
                      }
                      className="w-3.5 h-3.5 rounded border-gray-300 text-gray-600 focus:ring-gray-500 focus:ring-offset-0"
                    />
                    <span
                      className={`text-xs group-hover:text-gray-900 ${filters.appfolioStatus.includes("Not Started") ? "text-gray-900 font-medium" : "text-gray-600"}`}
                    >
                      Not Started
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.appfolioStatus.includes("Partial")}
                      onChange={() =>
                        handleToggleFilter("appfolioStatus", "Partial")
                      }
                      className="w-3.5 h-3.5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 focus:ring-offset-0"
                    />
                    <span
                      className={`text-xs group-hover:text-gray-900 ${filters.appfolioStatus.includes("Partial") ? "text-orange-700 font-medium" : "text-gray-600"}`}
                    >
                      In Process
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.appfolioStatus.includes("Completed")}
                      onChange={() =>
                        handleToggleFilter("appfolioStatus", "Completed")
                      }
                      className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500 focus:ring-offset-0"
                    />
                    <span
                      className={`text-xs group-hover:text-gray-900 ${filters.appfolioStatus.includes("Completed") ? "text-green-700 font-medium" : "text-gray-600"}`}
                    >
                      Completed
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL - CONTENT */}
        <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden min-h-0">
          {/* Sticky header with count */}
          {rememberedEmails.length > 0 && (
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-white">
              <span className="text-xs font-semibold text-gray-700">
                {rememberedEmails.length}{" "}
                {rememberedEmails.length === 1 ? "Learning" : "Learnings"}
              </span>
              {!isAtBottom && (
                <span className="text-[10px] text-gray-400 flex items-center gap-1 animate-pulse">
                  <ChevronDownIcon className="w-3 h-3" />
                  Scroll for more
                </span>
              )}
            </div>
          )}
          {/* Scroll area with bottom fade */}
          <div className="relative flex-1 min-h-0">
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="h-full overflow-y-auto scrollbar-hide p-4"
            >
              {rememberedEmails.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
                  <DocumentTextIcon className="w-12 h-12 text-gray-300 mb-3" />
                  <h3 className="text-sm font-medium text-gray-900 mb-1">
                    {hasActiveFilters
                      ? "No Matching Results"
                      : "No Learnings Yet"}
                  </h3>
                  <p className="text-xs text-gray-500 max-w-md">
                    {hasActiveFilters
                      ? "No learning notes match your current filters. Try adjusting or clearing the filters."
                      : 'When you edit email drafts and click "Remember", your changes will appear here so you can learn from your editing patterns.'}
                  </p>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="mt-3 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {rememberedEmails.map((email) => (
                    <div
                      key={email.id}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300"
                    >
                      {/* Card Header */}
                      <div
                        className="px-4 py-3 cursor-pointer hover:bg-gray-50/50 transition-colors"
                        onClick={() => handleToggleExpand(email.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded flex items-center justify-center">
                                <span className="text-[10px] font-bold text-gray-700">
                                  {email.subject.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-gray-900 truncate">
                                  {email.subject}
                                </h3>
                                <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-0.5">
                                  <span>To: {email.receiverEmail}</span>
                                  <span>•</span>
                                  <span>{formatDate(email.createdAt)}</span>
                                </div>

                                {/* Status Badges */}
                                <div className="flex items-center gap-1.5 mt-2">
                                  {email.showingStatus && (
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                        email.showingStatus === "Seen"
                                          ? "bg-green-100 text-green-700"
                                          : email.showingStatus === "Confirmed"
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-yellow-100 text-yellow-700"
                                      }`}
                                    >
                                      {email.showingStatus}
                                    </span>
                                  )}
                                  {email.appfolioStatus && (
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                        email.appfolioStatus === "Completed"
                                          ? "bg-green-100 text-green-700"
                                          : email.appfolioStatus === "Partial"
                                            ? "bg-orange-100 text-orange-700"
                                            : "bg-gray-100 text-gray-700"
                                      }`}
                                    >
                                      {email.appfolioStatus}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Difference Summary */}
                            {email.differenceSummary && (
                              <div className="bg-gray-50 border border-gray-100 rounded-lg p-2.5 mt-2">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
                                    Changes Made
                                  </span>
                                </div>
                                <div className="text-xs text-gray-700 leading-relaxed">
                                  {email.differenceSummary}
                                </div>
                              </div>
                            )}

                            {/* Notes Preview */}
                            {email.notes && expandedId !== email.id && (
                              <div className="mt-2 p-2 bg-gray-50 border border-gray-100 rounded-lg">
                                <div className="flex items-start gap-1.5">
                                  <div className="flex-1">
                                    <span className="text-[10px] font-medium text-gray-600">
                                      Notes:{" "}
                                    </span>
                                    <span className="text-[10px] text-gray-700">
                                      {email.notes}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(email.id);
                              }}
                              disabled={deletingId === email.id}
                              className="p-1.5 text-gray-400 hover:text-red-600 transition"
                            >
                              {deletingId === email.id ? (
                                <span className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin inline-block" />
                              ) : (
                                <TrashIcon className="w-3.5 h-3.5" />
                              )}
                            </button>
                            {expandedId === email.id ? (
                              <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {expandedId === email.id && (
                        <div className="border-t border-gray-200 bg-gradient-to-b from-gray-50/50 to-white">
                          {/* Notes Section */}
                          <div className="px-4 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded flex items-center justify-center">
                                  <PencilIcon className="w-3 h-3 text-gray-600" />
                                </div>
                                <label className="text-xs font-semibold text-gray-900">
                                  Your Notes
                                </label>
                              </div>
                              {editingNotesId !== email.id && (
                                <button
                                  onClick={() => handleEditNotes(email)}
                                  className="px-2.5 py-1 text-[10px] text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5 transition"
                                >
                                  <PencilIcon className="w-3 h-3" />
                                  {email.notes ? "Edit" : "Add"}
                                </button>
                              )}
                            </div>

                            {editingNotesId === email.id ? (
                              <div className="space-y-3">
                                <textarea
                                  value={editedNotes}
                                  onChange={(e) =>
                                    setEditedNotes(e.target.value)
                                  }
                                  rows={4}
                                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs text-gray-900 placeholder-gray-400 focus:border-gray-500 focus:ring-1 focus:ring-gray-200 focus:outline-none transition resize-none"
                                  placeholder="Add your notes about this change..."
                                />
                                <div className="flex gap-2 justify-end">
                                  <button
                                    onClick={handleCancelEditNotes}
                                    className="px-2.5 py-1 text-[10px] text-gray-600 hover:text-gray-800 border border-gray-300 rounded transition"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => handleSaveNotes(email.id)}
                                    disabled={savingNotes}
                                    className="px-2.5 py-1 text-[10px] bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-400 flex items-center gap-1.5 transition"
                                  >
                                    {savingNotes ? (
                                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <CheckIcon className="w-3 h-3" />
                                    )}
                                    Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="bg-white border border-gray-200 rounded-lg p-3 min-h-[60px]">
                                {email.notes ? (
                                  <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                                    {email.notes}
                                  </p>
                                ) : (
                                  <div className="text-center py-4">
                                    <p className="text-[10px] text-gray-400 italic">
                                      No notes added yet
                                    </p>
                                    <p className="text-[10px] text-gray-500 mt-0.5">
                                      Click "Add" to document your learning
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Email Comparison */}
                          <div className="px-4 py-4">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-6 h-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded flex items-center justify-center">
                                <DocumentTextIcon className="w-3 h-3 text-gray-600" />
                              </div>
                              <h3 className="text-xs font-semibold text-gray-900">
                                Email Comparison
                              </h3>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {/* Original Email */}
                              <div className="space-y-2">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                                  <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
                                    Original Email
                                  </label>
                                </div>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-60 overflow-y-auto">
                                  <pre className="text-[11px] text-gray-700 whitespace-pre-wrap break-words font-sans leading-relaxed">
                                    {email.originalBody ||
                                      "No original content available"}
                                  </pre>
                                </div>
                              </div>

                              {/* Edited Email */}
                              <div className="space-y-2">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                  <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
                                    Edited Email
                                  </label>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3 max-h-60 overflow-y-auto">
                                  <pre className="text-[11px] text-gray-700 whitespace-pre-wrap break-words font-sans leading-relaxed">
                                    {email.editedBody}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Bottom fade gradient — hidden when at bottom */}
            {rememberedEmails.length > 0 && !isAtBottom && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
            )}
          </div>
        </div>
      </div>

      <MessageBox
        title="Delete Learning"
        message="Are you sure you want to delete this learning? This action cannot be undone."
        isOpen={showDeleteConfirm}
        setIsOpen={setShowDeleteConfirm}
        okButtonText="Delete"
        cancelButtonText="Cancel"
        okButtonFunction={confirmDelete}
        cancelButtonFunction={() => setShowDeleteConfirm(false)}
        okButtonStyle={{ backgroundColor: "rgb(239, 68, 68)" }}
        iconColor={{ color: "rgb(239, 68, 68)" }}
      />
    </div>
  );
}
